/**
 * Client approvals' calls (R191, decision 1010 item 2; docs/specs/portal/client-approvals.md;
 * `20261103231500_client_approvals.sql`). Staff send and withdraw (`approval_send`, `approval_withdraw`) and read the
 * requests of a project or a contract (RLS); the client and Preview as client read through `portal_approvals` and
 * `portal_approval`, decide with `approval_decide`, and sign a contract through the `docuseal` function
 * (`approval-sign`). Files: a Safety version from the `safety` bucket, a contract's PDF and its signed copy from the
 * `approvals` bucket, each a 5-minute link the storage policies allow. The database decides who may do what.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { ApprovalKind, ApprovalRow, BlockSnapshot } from '@teslyn/domain';

/** A request as the client portal lists it. */
export interface PortalApproval extends ApprovalRow {
  readonly project_number: string;
  readonly project_title: string;
  readonly signer_name: string;
  readonly signed_available: boolean;
  readonly size_bytes: number | null;
  /** This reader approves, declines or signs here (a client admin, not a preview, still waiting). */
  readonly can_act: boolean;
}

/** One request in full: the block change's before and after, the file's path. */
export interface PortalApprovalDetail extends PortalApproval {
  readonly snapshot: BlockSnapshot | null;
  readonly before_snapshot: BlockSnapshot | null;
  readonly storage_path: string | null;
}

/** A request as staff read it (the table's own columns). */
export interface StaffApproval extends ApprovalRow {
  readonly signer_name: string;
  readonly signed_available: boolean;
  readonly size_bytes: number | null;
  readonly storage_path: string | null;
  readonly snapshot: BlockSnapshot | null;
  readonly before_snapshot: BlockSnapshot | null;
  readonly signing_message: string;
  /** A contract (decision 1014): the manager who signs second. */
  readonly manager_user_id?: string | null;
}

/** A manager of the operator who may sign a contract second (decision 1014). */
export interface ContractSigner {
  readonly user_id: string;
  readonly name: string;
}

/** The operator's managers to choose from when a contract is sent to sign. */
export async function loadContractSigners(sb: SupabaseClient, tenantId: string): Promise<ContractSigner[]> {
  const { data, error } = await sb.rpc('approval_contract_signers', { p_tenant: tenantId });
  if (error) fail(error, 'The managers could not be read.');
  return (Array.isArray(data) ? data : []) as ContractSigner[];
}

async function functionWords(error: unknown, fallback: string): Promise<string> {
  const context = (error as { context?: Response } | null)?.context;
  const body = context && typeof context.json === 'function' ? await context.json().catch(() => null) : null;
  const w = body && typeof body === 'object' ? (body as { error?: unknown }).error : null;
  return typeof w === 'string' && w ? w : fallback;
}

const fail = (error: { message?: string } | null, fallback: string): never => {
  throw new Error(error?.message || fallback);
};

// ------------------------------------------------------------------------------------------------ staff

const STAFF_COLUMNS =
  'id, tenant_id, client_org_id, project_id, kind, item_id, title, version, version_hash, file_name, size_bytes, ' +
  'note, state, sent_at, sent_by_name, decided_at, decided_by_name, decline_reason, superseded_by, closed_at, ' +
  'signing_state, signing_message, signer_name, signed_available, storage_path, snapshot, before_snapshot, ' +
  // decision 1014: the operator's manager who signs second
  'manager_user_id, manager_name, client_signed_at, manager_signed_at, declined_by';

/** A project's requests, newest first (staff). */
export async function loadProjectApprovals(sb: SupabaseClient, projectId: string): Promise<StaffApproval[]> {
  const { data, error } = await sb
    .from('approval_request')
    .select(STAFF_COLUMNS)
    .eq('project_id', projectId)
    .order('sent_at', { ascending: false });
  if (error) fail(error, 'The approvals could not be read.');
  return (data ?? []) as unknown as StaffApproval[];
}

/** A contract's requests to sign, newest first (the commercial staff). */
export async function loadContractApprovals(
  sb: SupabaseClient,
  contractId: string,
): Promise<StaffApproval[]> {
  const { data, error } = await sb
    .from('approval_request')
    .select(STAFF_COLUMNS)
    .eq('kind', 'contract')
    .eq('item_id', contractId)
    .order('sent_at', { ascending: false });
  if (error) fail(error, 'The contract’s signing could not be read.');
  return (data ?? []) as unknown as StaffApproval[];
}

/** Each block's SHA-256 of what it would send now (a block changed since it was sent shows so). */
export async function loadBlockHashes(sb: SupabaseClient, projectId: string): Promise<Map<string, string>> {
  const { data, error } = await sb.rpc('approval_block_hashes', { p_project: projectId });
  if (error) fail(error, 'The blocks could not be read.');
  const rows = (Array.isArray(data) ? data : []) as { block_id: string; version_hash: string }[];
  return new Map(rows.map((r) => [r.block_id, r.version_hash]));
}

/** "Send to client for approval": a block, a Safety version (the item's own file and SHA-256). */
export async function sendApproval(
  sb: SupabaseClient,
  input: { readonly kind: Exclude<ApprovalKind, 'contract'>; readonly itemId: string; readonly note: string },
): Promise<StaffApproval> {
  const { data, error } = await sb.rpc('approval_send', {
    p_id: crypto.randomUUID(),
    p_kind: input.kind,
    p_item: input.itemId,
    p_note: input.note,
    p_file_name: null,
    p_size: null,
    p_sha256: null,
  });
  if (error) fail(error, 'Teslyn Cloud could not send it to the client.');
  return data as StaffApproval;
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', bytes as unknown as ArrayBuffer);
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** "Send to the client to sign": the contract's PDF (with the client's DocuSeal tags) kept, then the request. */
export async function sendContractApproval(
  sb: SupabaseClient,
  input: {
    readonly tenantId: string;
    readonly contractId: string;
    readonly pdf: Uint8Array;
    readonly fileName: string;
    readonly note: string;
    /** The manager who signs after the client (decision 1014); null: the sender, who is a manager. */
    readonly managerUserId: string | null;
  },
): Promise<StaffApproval> {
  const id = crypto.randomUUID();
  const path = `tenants/${input.tenantId}/approvals/${id}.pdf`;
  const up = await sb.storage
    .from('approvals')
    .upload(path, new Blob([input.pdf as unknown as ArrayBuffer], { type: 'application/pdf' }), {
      contentType: 'application/pdf',
      upsert: false,
    });
  if (up.error) fail(up.error, 'The contract PDF could not be kept in Teslyn Cloud.');
  const { data, error } = await sb.rpc('approval_send_contract', {
    p_id: id,
    p_contract: input.contractId,
    p_note: input.note,
    p_file_name: input.fileName,
    p_size: input.pdf.length,
    p_sha256: await sha256Hex(input.pdf),
    p_manager: input.managerUserId,
  });
  if (error) fail(error, 'Teslyn Cloud could not send the contract to the client.');
  return data as StaffApproval;
}

/** Withdraw a waiting request; a contract the client began signing is archived in DocuSeal first. */
export async function withdrawApproval(
  sb: SupabaseClient,
  r: Pick<StaffApproval, 'id' | 'kind' | 'signing_state'>,
): Promise<void> {
  if (r.kind === 'contract' && (r.signing_state === 'sending' || r.signing_state === 'sent')) {
    const { error } = await sb.functions.invoke('docuseal/approval-withdraw', { body: { request: r.id } });
    if (error)
      throw new Error(
        await functionWords(error, 'Teslyn Cloud could not reach DocuSeal. Try again shortly.'),
      );
    return;
  }
  const { error } = await sb.rpc('approval_withdraw', { p_id: r.id });
  if (error) fail(error, 'The request could not be withdrawn.');
}

// ------------------------------------------------------------------------------------------------ client

/** The organisation's requests (one project's, or every one), as the client or Preview as client sees them. */
export async function loadPortalApprovals(
  sb: SupabaseClient,
  clientOrgId: string,
  projectId: string | null,
  asAdmin = false,
): Promise<PortalApproval[]> {
  const { data, error } = await sb.rpc('portal_approvals', {
    p_org: clientOrgId,
    p_project: projectId,
    p_as_admin: asAdmin,
  });
  if (error) fail(error, 'The approvals could not be read.');
  return (Array.isArray(data) ? data : []) as PortalApproval[];
}

export async function loadPortalApproval(
  sb: SupabaseClient,
  id: string,
  asAdmin = false,
): Promise<PortalApprovalDetail> {
  const { data, error } = await sb.rpc('portal_approval', { p_id: id, p_as_admin: asAdmin });
  if (error) fail(error, 'The request could not be read.');
  return data as PortalApprovalDetail;
}

/** Approve, or decline with the reason (a client admin). */
export async function decideApproval(
  sb: SupabaseClient,
  id: string,
  approve: boolean,
  reason: string,
): Promise<PortalApproval> {
  const { data, error } = await sb.rpc('approval_decide', { p_id: id, p_approve: approve, p_reason: reason });
  if (error) fail(error, 'Teslyn Cloud could not record your answer. Try again.');
  return data as PortalApproval;
}

/** "Sign with DocuSeal": the client admin's signing page in DocuSeal, or the manager's once the client signed. */
export async function contractSigningUrl(sb: SupabaseClient, id: string): Promise<string> {
  const { data, error } = await sb.functions.invoke<{ url?: string }>('docuseal/approval-sign', {
    body: { request: id },
  });
  if (error || !data?.url)
    throw new Error(await functionWords(error, 'DocuSeal could not be reached. Try again shortly.'));
  return data.url;
}

/** "Check DocuSeal now" on a contract sent to sign. */
export async function refreshContractSigning(sb: SupabaseClient, id: string): Promise<void> {
  const { error } = await sb.functions.invoke('docuseal/approval-refresh', { body: { request: id } });
  if (error) throw new Error(await functionWords(error, 'DocuSeal could not be reached. Try again shortly.'));
}

// ------------------------------------------------------------------------------------------------ files

/** A 5-minute link to the file the request is about: the Safety version, or the contract PDF sent. */
export async function approvalFileUrl(
  sb: SupabaseClient,
  r: { readonly kind: ApprovalKind; readonly storage_path: string | null; readonly file_name: string },
): Promise<string> {
  if (!r.storage_path) throw new Error('This request has no file.');
  const { data, error } = await sb.storage
    .from(r.kind === 'contract' ? 'approvals' : 'safety')
    .createSignedUrl(r.storage_path, 300, { download: r.file_name || true });
  if (error || !data) fail(error, 'The document could not be opened.');
  return data!.signedUrl;
}

/** The copy DocuSeal signed of a contract (kept once everyone signed). */
export async function signedContractUrl(
  sb: SupabaseClient,
  r: { readonly id: string; readonly tenant_id: string; readonly title: string },
): Promise<string> {
  const { data, error } = await sb.storage
    .from('approvals')
    .createSignedUrl(`tenants/${r.tenant_id}/approvals/${r.id}/signed.pdf`, 300, {
      download: `${r.title.replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_')}_Signed.pdf`,
    });
  if (error || !data) fail(error, 'The signed copy could not be opened.');
  return data!.signedUrl;
}
